import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Operadora, Usuario, UsuarioPerfil, VinculoUsuarioOperadora } from '@gestor-web/@core/api/ords/model';
import { PapelAcessoItem } from '@gestor-web/@core/api/ords/model/papel-acesso';
import { CustomValidators } from '@gestor-web/@core/utils';
import { resolverAbrangencia } from '@gestor-web/@core/auth/abrangencia';

@Component({
    selector: 'bl-profile-form',
    templateUrl: './profile-form.component.html',
    styleUrls: ['./profile-form.component.scss'],
    standalone: false
})
export class ProfileFormComponent implements OnChanges {
  form: UntypedFormGroup;
  localFoto: string;
  operadoraPreferencial: Operadora | VinculoUsuarioOperadora;
  exibeOperadoraPreferencial = false;

  @Input() usuario: Usuario;
  @Input() disable: boolean;
  @Input() showClear: boolean;
  @Input() hideSensitiveInputs: boolean;
  @Input() showOperadoraPreferencial: boolean = false;
  @Input() canEditEmail: boolean = true;
  @Input() operadoras: Operadora[] | VinculoUsuarioOperadora[] = [];
  @Input() papeisAcesso: PapelAcessoItem[] = [];
  @Output() submitEvent: EventEmitter<Usuario>;
  @Output() cancelEditEvent: EventEmitter<void>;

  constructor(private fb: UntypedFormBuilder) {
    this.submitEvent = new EventEmitter<Usuario>();
    this.cancelEditEvent = new EventEmitter<void>();
    this.form = this.createForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disable']) {
      if (this.disable) {
        this.form.disable({ onlySelf: this.disable });
      } else {
        this.form.enable({ onlySelf: this.disable });
        if (!this.canEditEmail) {
          this.form.controls.ds_email.disable();
        }
      }
    }

    if (changes['usuario']) {
      this.form.patchValue({ ...this.usuario, ib_situacao: !!this.usuario?.ib_situacao });
      this.localFoto = this.usuario?.im_foto;
    }

    if (changes['usuario'] || changes['showOperadoraPreferencial']) {
      this.exibeOperadoraPreferencial =
        this.showOperadoraPreferencial && resolverAbrangencia(this.usuario as UsuarioPerfil) !== 'NENHUMA';
    }

    if (changes['operadoras']) {
      if (!this.operadoras) {
        this.operadoraPreferencial = null;
      } else {
        const perfil = this.usuario as UsuarioPerfil;
        for (const op of this.operadoras) {
          if (op.cd_registro_ans == perfil.cd_registro_ans_preferencial) {
            this.operadoraPreferencial = op;
            break;
          }
        }
      }
    }
  }

  onSubmit(): void {
    const formData = this.form.getRawValue();
    Object.assign(formData, { ib_situacao: +formData.ib_situacao });
    if (this.showOperadoraPreferencial) {
      Object.assign(formData, { cd_registro_ans_preferencial: this.operadoraPreferencial?.cd_registro_ans });
    }
    this.submitEvent.emit(formData);
  }

  onCancelEdit() {
    // console.log('[ProfileForm] onCancelEdit');
    this.form.patchValue({ ...this.usuario, ib_situacao: !!this.usuario?.ib_situacao });
    if (!this.operadoras) {
      this.operadoraPreferencial = null;
    } else {
      const perfil = this.usuario as UsuarioPerfil;
      for (const op of this.operadoras) {
        if (op.cd_registro_ans == perfil.cd_registro_ans_preferencial) {
          this.operadoraPreferencial = op;
          break;
        }
      }
    }
    this.cancelEditEvent.emit();
  }

  get foto(): string {
    return this.form.get('im_foto').value;
  }

  get situacao(): string {
    return this.form.get('ib_situacao').value;
  }

  fotoChanged($event: Event) {
    const input = $event.target as HTMLInputElement;
    if (!input.files.length) {
      return;
    }

    const file = input.files[0];
    const reader = new FileReader();
    reader.onloadend = e => {
      const fotoFormControl = this.form.get('im_foto');
      fotoFormControl.setValue((e.target as FileReader).result as string);
      fotoFormControl.markAsDirty();
    };
    reader.readAsDataURL(file);
  }

  onChangeOperadora(operadora: Operadora | VinculoUsuarioOperadora) {
    // console.log('[ProfileForm] onChangeOperadora', operadora);
    this.operadoraPreferencial = operadora;
    this.form.markAsDirty();
  }

  private createForm(): UntypedFormGroup {
    return this.fb.group({
      cd_usuario: [''],
      cd_usuario_keycloak: [''],
      cd_papel_acesso: [''],
      ds_cargo: [''],
      ds_email: new FormControl<string>({ value: '', disabled: !this.canEditEmail }, [
        Validators.required,
        Validators.email,
      ]),
      dt_alteracao: [''],
      dt_inclusao: [''],
      ib_situacao: [true],
      im_foto: [''],
      nm_usuario: ['', [Validators.required]],
      nr_cpf: ['', [this.optionalCpfValidator]],
      nr_fone_celular: [''],
      nr_fone_comercial: [''],
    });
  }
  private optionalCpfValidator(control: AbstractControl) {
    const value = control?.value;
    if (value === null || value === undefined || value === '') return null;
    return CustomValidators.validateCPF(control);
  }
}
